import { atom } from 'nanostores';

export type Theme = 'dark' | 'light';

export const kTheme = 'alpha_theme';

/**
 * Resolves the system color-scheme preference to 'dark' or 'light'.
 * Used as the default when no theme has been persisted yet.
 */
function getSystemTheme(): Theme {
  if (typeof window !== 'undefined') {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return 'dark'; // SSR fallback
}

export function themeIsDark() {
  return themeStore.get() === 'dark';
}

export const DEFAULT_THEME: Theme = 'dark';

export const themeStore = atom<Theme>(initStore());

function initStore(): Theme {
  if (!import.meta.env.SSR) {
    const persistedTheme = localStorage.getItem(kTheme);

    // If the user previously had 'system' stored, resolve it now
    if (persistedTheme === 'system') {
      const resolved = getSystemTheme();
      localStorage.setItem(kTheme, resolved);
      return resolved;
    }

    if (persistedTheme === 'dark' || persistedTheme === 'light') {
      return persistedTheme;
    }

    // No persisted value — use system preference as default
    return getSystemTheme();
  }

  return DEFAULT_THEME;
}

export function setTheme(newTheme: Theme) {
  themeStore.set(newTheme);
  localStorage.setItem(kTheme, newTheme);
  document.querySelector('html')?.setAttribute('data-theme', newTheme);
}

export function toggleTheme() {
  const currentTheme = themeStore.get();
  const newTheme = currentTheme === 'dark' ? 'light' : 'dark';
  setTheme(newTheme);
}

